import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { GoogleLogo, ArrowRight, Sun, Moon } from "@phosphor-icons/react";

export default function Login() {
  const { login, googleLogin } = useAuth();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background relative">
      <button
        onClick={toggle}
        className="absolute top-4 right-4 z-10 p-2 rounded-sm border border-border bg-card/70 backdrop-blur hover:bg-muted transition-colors"
        data-testid="login-theme-toggle"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun size={18} weight="duotone" /> : <Moon size={18} weight="duotone" />}
      </button>
      <div className="hidden lg:block relative brand-grad overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <div />
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--uc-primary)]">UC GTM App</div>
            <h2 className="font-display text-4xl font-black mt-2 leading-tight">Quote, sell, and grow — all from one secure portal.</h2>
            <p className="text-muted-foreground mt-3 max-w-md">Sign in to manage your customers, build quotes, and order from the catalog.</p>
          </div>
          <div />
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Welcome back</div>
          <h1 className="font-display text-4xl font-black mt-1 tracking-tight">Sign in to UC GTM App</h1>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1" data-testid="login-email" placeholder="you@partner.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white h-11" data-testid="login-submit">
              {loading ? "Signing in…" : <>Sign in <ArrowRight size={16} className="ml-2" /></>}
            </Button>
          </form>
          <div className="flex items-center my-6 text-xs text-muted-foreground">
            <div className="flex-1 border-t border-border" />
            <span className="px-3">OR</span>
            <div className="flex-1 border-t border-border" />
          </div>
          <Button variant="outline" className="w-full rounded-sm h-11" onClick={googleLogin} data-testid="login-google">
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>
          <div className="mt-6 text-sm text-muted-foreground text-center" data-testid="login-invite-note">
            Invite-only · Contact your Partner Success Manager for access.
          </div>
        </div>
      </div>
    </div>
  );
}
